import React, { useMemo } from 'react';

import { Typography, Skeleton } from '@mui/material';
import startCase from 'lodash/startCase';
import { useHistory } from 'react-router';

import Table, { ColumnOption, renderDate } from 'domains/core/components/Table';
import TableOffsetPagination from 'domains/core/components/TableOffsetPagination';
import useContactColumns from 'hooks/queries/useContactColumns';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { URLPaths, SortDirection, SQLType } from 'models/enums';
import { Contact, ContactColumn } from 'models/types';
import {
    getAgeFromBirthday,
    getFormattedAddress,
    getFormattedPhoneNumber,
    ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS,
} from 'utils';

const loadingColumns: ColumnOption[] = [
    { Header: 'First Name', accessor: 'first_name', align: 'left' },
    { Header: 'Last Name', accessor: 'last_name', align: 'left' },
    { Header: 'Email', accessor: 'email', align: 'left' },
    { Header: 'Status', accessor: 'subscription_status', align: 'center' },
    { Header: 'Date of Birth', accessor: 'birth_date', align: 'center' },
];

type ContactColumnWithSort = ContactColumn & {
    disableSortBy: boolean;
};

type Props = {
    contacts: any[];
    totalContacts: number;
    onSort?: (sortColumn: string, direction: SortDirection) => void;
    page: number;
    totalPages: number;
    onPreviousPage: () => void;
    onNextPage: () => void;
    isFetching?: boolean;
    contactsPerPage: number;
    onContactsPerPageChange: (contactsPerPage: number) => void;
};

const underscoreRegex = /_+/g;
const underscoreReplace = (value: string): string => value?.replace(underscoreRegex, ' ');

/**
 * A table used for displaying contacts in the contacts page.
 *
 * @param contacts The contacts data used for the table.
 * @param onSort Function used to re-query and sort the contacts table.
 *
 * @returns The React node created by this component.
 */
const ContactsTable = ({
    contacts,
    totalContacts,
    onSort,
    page,
    totalPages,
    onPreviousPage,
    onNextPage,
    isFetching = false,
    contactsPerPage,
    onContactsPerPageChange,
}: Props) => {
    const history = useHistory<any>();
    const route = history.location.pathname;
    const disableSortBy = route === URLPaths.SEGMENTS_CONTACTS;

    const { data: contactColumns, isLoading } = useContactColumns();
    const { canViewContactsFullPhi } = useAccessCentrePermissions();

    // Helper to wrap cell renderers with skeleton when fetching
    const wrapCellWithSkeleton = (cellRenderer?: (props: any) => React.ReactNode) => (props: any) => {
        if (isFetching) {
            return <Skeleton width="80%" />;
        }
        if (cellRenderer) {
            return cellRenderer(props);
        }
        // Default: return the value directly
        return props.value ?? '';
    };

    const sortByMostRecentVisit = (rowA: any, rowB: any, columnId: string, desc: boolean) => {
        const minDate = new Date(-8640000000000000);
        const aDate = rowA?.original.most_recent_visit ? new Date(rowA.original.most_recent_visit) : minDate;
        const bDate = rowB?.original.most_recent_visit ? new Date(rowB.original.most_recent_visit) : minDate;

        return aDate.getTime() - bDate.getTime();
    };

    const data: object[] = useMemo(
        () =>
            contacts.map((contact) => ({
                ...contact,
                birth_date: contact.birth_date ? new Date(contact.birth_date) : '',
            })),
        [contacts]
    );

    const columns: ColumnOption[] = useMemo(() => {
        // We may be able to remove this after https://cured.atlassian.net/browse/COC-381 is completed, and blank columns are not shown.
        const style = {};
        const noSortColumns = ['first_name', 'last_name', 'email', 'street_address'];
        //Filtered columns is contactColumns, but mutated to include cases for age and address
        let filteredColumns: ContactColumnWithSort[] =
            contactColumns?.map((contactColumn) => ({
                ...contactColumn,
                disableSortBy: disableSortBy || noSortColumns.includes(contactColumn.columnName),
            })) ?? [];

        // Add email_subscription_status column which is not part of metadata.
        filteredColumns.push({
            columnName: 'email_subscription_status',
            isCustom: false,
            isMergeTag: false,
            isPHI: false,
            isSegmentable: false,
            personalizationDefault: '',
            sqlType: SQLType.CHARACTER_VARYING,
            disableSortBy: true,
        });
        filteredColumns.push({
            columnName: 'sms_subscription_status',
            isCustom: false,
            isMergeTag: false,
            isPHI: false,
            isSegmentable: false,
            personalizationDefault: '',
            sqlType: SQLType.CHARACTER_VARYING,
            disableSortBy: true,
        });
        filteredColumns.push({
            columnName: 'voice_subscription_status',
            isCustom: false,
            isMergeTag: false,
            isPHI: false,
            isSegmentable: false,
            personalizationDefault: '',
            sqlType: SQLType.CHARACTER_VARYING,
            disableSortBy: true,
        });

        // Remove is_deactivated column from display
        filteredColumns = filteredColumns.filter((column) => !['is_deactivated'].includes(column.columnName));

        // Access Centre: PHI-marked columns only when user has contacts.view + contacts.view_phi
        if (!canViewContactsFullPhi) {
            filteredColumns = filteredColumns.filter((column) => !column.isPHI);
        }

        return filteredColumns
            .flatMap((column: ContactColumnWithSort, index: number): any => {
                //Switch case for special cases based on column's type
                switch (column.sqlType) {
                    case SQLType.CHARACTER_VARYING_ARRAY:
                        return {
                            Header: underscoreReplace(column.columnName),
                            accessor: column.columnName,
                            align: 'center' as const,
                            style,
                            order: 99,
                            disableSortBy: column.disableSortBy,
                            Cell: ({ value }: any) => value?.join(','),
                        };
                    case 'character varying':
                        if (column.columnName === 'address') {
                            return {
                                id: index.toString(),
                                Header: 'Address',
                                accessor: (originalRow: Contact, rowIndex: any) => getFormattedAddress(originalRow),
                                align: 'center' as const,
                                style,
                                order: 5,
                                customSort: 'street_address',
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'subscription_status') {
                            return {
                                Header: 'Subscription Status',
                                accessor: 'subscription_status',
                                align: 'center' as const,
                                Cell: ({ value: status }: any) => (
                                    <Typography
                                        color={status === 'active' ? 'success.content' : 'warning.contrastText'}
                                    >
                                        {startCase(status)}
                                    </Typography>
                                ),
                                style,
                                order: 99,
                                customSort: 'cs.status',
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'phone_number') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                //Formats US phone numbers in the cell
                                Cell: ({ value: phone_number }: { value: string }) =>
                                    getFormattedPhoneNumber(phone_number),
                                style,
                                order: 4,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'first_name') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 1,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'last_name') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 2,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'email') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 3,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'street_address') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 5,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'street_address_2') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 6,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'state_code') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 7,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'zip_code') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 8,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'administrative_gender') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 11,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'race') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 12,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'preferred_contact_method') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 13,
                                disableSortBy,
                            };
                        } else {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'left' as const,
                                style,
                                order: 99,
                                disableSortBy: column.disableSortBy,
                            };
                        }
                    case 'double precision':
                    case 'integer':
                        return {
                            Header: underscoreReplace(column.columnName),
                            accessor: column.columnName,
                            align: 'right' as const,
                            style,
                            order: 99,
                            disableSortBy: column.disableSortBy,
                        };
                    case 'date':
                    case 'timestamp with time zone':
                        if (column.columnName === 'most_recent_visit') {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'center' as const,
                                sortType: sortByMostRecentVisit,
                                Cell: ({ value }: { value: string }) => renderDate({ value: new Date(value) }),
                                style,
                                order: 99,
                                disableSortBy: column.disableSortBy,
                            };
                        } else if (column.columnName === 'birth_date') {
                            const birthDateColumns: any[] = [
                                {
                                    id: 'birth_date',
                                    Header: underscoreReplace(column.columnName),
                                    accessor: column.columnName,
                                    sortType: 'datetime' as const,
                                    align: 'left' as const,
                                    Cell: renderDate,
                                    style,
                                    order: 9,
                                    customSort: 'birth_date',
                                    disableSortBy: column.disableSortBy,
                                },
                            ];

                            const ageColumnExists = filteredColumns.some((c) => c.columnName === 'age');
                            if (!ageColumnExists) {
                                birthDateColumns.push({
                                    id: 'age',
                                    Header: 'Age',
                                    accessor: 'birth_date',
                                    align: 'center' as const,
                                    sortType: 'datetime' as const,
                                    sortInverted: 'true',
                                    Cell: ({ value }: any) => getAgeFromBirthday(value),
                                    style,
                                    order: 10,
                                    customSort: 'birth_date',
                                    disableSortBy: true,
                                });
                            }

                            return birthDateColumns;
                        } else {
                            return {
                                Header: underscoreReplace(column.columnName),
                                accessor: column.columnName,
                                align: 'center' as const,
                                style,
                                order: 99,
                                disableSortBy: column.disableSortBy,
                            };
                        }
                    case 'boolean':
                        return {
                            Header: underscoreReplace(column.columnName.replace(/^is_/g, '')),
                            accessor: column.columnName,
                            Cell: ({ value }: any) => (typeof value == 'boolean' ? value?.toString() : ''),
                            align: 'center' as const,
                            style,
                            order: 99,
                            disableSortBy: column.disableSortBy,
                        };
                    default:
                        return {
                            Header: underscoreReplace(column.columnName),
                            accessor: column.columnName,
                            align: 'center' as const,
                            style,
                            order: 99,
                            disableSortBy: column.disableSortBy,
                        };
                }
            })
            .sort((a, b) => {
                if (a.order < b.order) {
                    return -1;
                }
                if (a.order > b.order) {
                    return 1;
                }
                return 0;
            })
            .map((column) => ({
                ...column,
                Cell: wrapCellWithSkeleton(column.Cell),
            }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isFetching, canViewContactsFullPhi]);

    if (isLoading) {
        return (
            <div data-testid="contacts-table-loading" role="status" aria-label="Loading contacts">
                <Table columns={loadingColumns} data={[]} isLoading />
            </div>
        );
    }

    if (contacts.length === 0) {
        return null;
    }

    return (
        <>
            <Table
                data={data}
                columns={columns}
                defaultSortingRules={[
                    { id: 'subscription_status', desc: false },
                    { id: 'last_name', desc: false },
                ]}
                manualSortBy
                onSort={(columnId, direction) => {
                    onSort(columnId, direction);
                }}
            />
            <TableOffsetPagination
                page={page}
                totalPages={totalPages}
                onPreviousPage={onPreviousPage}
                onNextPage={onNextPage}
                rowsPerPage={contactsPerPage}
                rowsPerPageOptions={ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS}
                onRowsPerPageChange={onContactsPerPageChange}
                testIdPrefix="contacts-table-pagination"
            />
        </>
    );
};

export default ContactsTable;
